import { useCallback, useEffect, useRef, useState } from 'react';
import { useRestClient } from '../api/RestClientContext';
import {
    AuthStatus,
    LoginRequest,
    LoginResponse,
    LogoutResponse
} from '../models/auth';

interface UseAuthOptions {
    onAuthChange?: (isAuthenticated: boolean) => void;
}

function defaultAuthStatus(success: boolean = false): AuthStatus {
    return { success: success, result: { username: '' } };
}

export function useAuth(options: UseAuthOptions = {}) {
    const { onAuthChange } = options;
    const client = useRestClient();
    const [authStatus, setAuthStatus] = useState<AuthStatus>(defaultAuthStatus());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const didRun = useRef(false);

    const checkAuthStatus = useCallback(async () => {
        console.log('🔍 checkAuthStatus вызван');
        try {
            setLoading(true);
            setError(null);
            const status = await client.checkAuthStatus();
            console.log('📊 Статус от сервера:', status);
            setAuthStatus(status);
        } catch (err) {
            console.log('❌ Ошибка checkAuthStatus:', err);
            setError(err instanceof Error ? err.message : 'Ошибка проверки авторизации');
            setAuthStatus(defaultAuthStatus());
            onAuthChange?.(false);
        } finally {
            setLoading(false);
        }
    }, [client, onAuthChange]);

    const login = useCallback(async (credentials: LoginRequest): Promise<LoginResponse> => {
        try {
            setLoading(true);
            setError(null);
            const response = await client.login(credentials);

            if (response.success) {
                console.log('✅ Логин успешен, устанавливаем isAuthenticated: true');
                setAuthStatus(response);
                onAuthChange?.(true);
            }

            return response;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Ошибка авторизации';
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [client, onAuthChange]);

    const logout = useCallback(async (): Promise<LogoutResponse> => {
        try {
            setLoading(true);
            setError(null);
            const response = await client.logout();

            setAuthStatus(defaultAuthStatus());
            onAuthChange?.(false);

            return response;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Ошибка выхода';
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [client, onAuthChange]);

    const isAuthenticated = useCallback(() => {
        return client.isAuthenticated();
    }, [client]);

    useEffect(() => {
        if (!didRun.current) {
            didRun.current = true;
            checkAuthStatus();
        }
    }, [checkAuthStatus]);

    return {
        // Состояние
        authStatus,
        loading,
        error,
        isAuthenticated: authStatus,

        // Методы
        login,
        logout,
        checkAuthStatus,
        isAuthenticatedLocal: isAuthenticated,
    };
}